// src/priceUpdater.js

export function updatePrices(teams = [], netDemand = {}, gameResults = []) {
  const now = new Date();
  const timestamp = now.toISOString();
  const dateStr = timestamp.split("T")[0];

  const teamMap = new Map();
  teams.forEach((t) => {
    const key = t.id || t.name;
    if (key) teamMap.set(key, t);
  });

  return teams.map((team) => {
    const teamKey = team.id || team.name;
    
    let winsDelta = 0;
    let lossesDelta = 0;
    let lastGameResult = team.lastGame || null;

    gameResults.forEach((game) => {
      const { winnerId, loserId, margin = 1 } = game;

      if (teamKey === winnerId) {
        winsDelta += 1;
        const loser = teamMap.get(loserId);
        lastGameResult = {
          opponent: loser?.name || loserId,
          outcome: "W",
          margin,
          date: dateStr,
        };
      } else if (teamKey === loserId) {
        lossesDelta += 1;
        const winner = teamMap.get(winnerId);
        lastGameResult = {
          opponent: winner?.name || winnerId,
          outcome: "L",
          margin,
          date: dateStr,
        };
      }
    });

    const currentFpi = team.fpi ?? 0;
    const currentProjW = team.proj_w ?? 6.0;
    const currentPlayoffPct = team.playoff_pct ?? 10;

    // 2. Base Price Formula Calculation
    let basePrice = 10.0 + (4 * currentFpi) + (5 * currentProjW) + (1 * currentPlayoffPct);

    // 3. Non-Linear (Square Root) Slippage Effect
    const rawDemand = netDemand[teamKey] || netDemand[team.id] || netDemand[team.name] || 0;
    
    const sign = rawDemand < 0 ? -1 : 1;
    const dampenedMagnitude = Math.sqrt(Math.abs(rawDemand));
    const effectiveDemand = sign * dampenedMagnitude;

    const impactFactor = 0.01; 
    const demandMultiplier = 1 + (effectiveDemand * impactFactor);
    
    let midPrice = basePrice * demandMultiplier;

    // 4. Price Guardrails ($1.00 min, $500.00 max) on the midPrice
    midPrice = Math.max(1.00, Math.min(midPrice, 500.00));

    // 5. Asymmetrical Bid-Ask Spread & Anti-Exploit Safeguard
    const spreadPercent = 0.02; 
    
    // Establish the baseline un-impacted or previous buy price to anchor the sell price
    const standardBuy = midPrice * (1 + spreadPercent / 2);
    const previousBuyPrice = team.buy_price || standardBuy;

    let buyPrice = parseFloat(standardBuy.toFixed(2));
    
    // ANTI-GLITCH RULE: If there is buying pressure, anchor the sell price to the 
    // previous buy price so it doesn't instantly inflate alongside the new purchase.
    let sellPrice = parseFloat(previousBuyPrice.toFixed(2));

    // If demand is negative (heavy selling), allow the market sell price to drop normally
    const standardSell = midPrice * (1 - spreadPercent / 2);
    if (rawDemand < 0) {
      sellPrice = parseFloat(standardSell.toFixed(2));
      buyPrice = parseFloat((sellPrice * (1 + spreadPercent)).toFixed(2));
    }

    // Strict safety check: Ensure the sell price can never meet or exceed the buy price
    if (sellPrice >= buyPrice) {
      sellPrice = parseFloat((buyPrice * (1 - spreadPercent)).toFixed(2));
    }

    const finalMidPrice = parseFloat(midPrice.toFixed(2));

    // 6. Track Price History (using the mid-price reference)
    let updatedHistory = Array.isArray(team.price_history) ? [...team.price_history] : [];
    const lastRecordedPrice = updatedHistory.length > 0 ? updatedHistory[updatedHistory.length - 1].price : team.price;

    if (finalMidPrice !== lastRecordedPrice) {
      updatedHistory.push({
        date: dateStr,
        timestamp,
        price: finalMidPrice,
      });
    }

    return {
      ...team,
      fpi: parseFloat(currentFpi.toFixed(2)),
      proj_w: parseFloat(currentProjW.toFixed(1)),
      playoff_pct: parseFloat(currentPlayoffPct.toFixed(1)),
      price: finalMidPrice,
      buy_price: buyPrice,   
      sell_price: sellPrice, 
      wins: (team.wins || 0) + winsDelta,
      losses: (team.losses || 0) + lossesDelta,
      lastGame: lastGameResult,
      price_history: updatedHistory,
      strength_rating: finalMidPrice,
    };
  });
}